import {
  Autocomplete,
  Checkbox,
  Chip,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import {
  GridFilterInputValueProps,
  GridFilterItem,
} from '@mui/x-data-grid-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro';
import { useImperativeHandle, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';

import { FilterService } from './CustomFilter.interface';

const SELECTED_STYLE = { backgroundColor: '#edf2f5' };
const WidePopper = (props: React.ComponentProps<typeof Popper>) => (
  <Popper
    {...props}
    style={{
      ...props.style,
      width: 'max-content',
      maxWidth: 'min(90vw, 800px)',
    }}
  />
);

interface ComponentProps extends GridFilterInputValueProps {
  service: FilterService;
}

interface DateRangeFilterProps extends GridFilterInputValueProps {
  item: GridFilterItem & {
    value?: [Date | null, Date | null];
  };
  applyValue: (value: GridFilterItem) => void;
  size?: 'small' | 'medium';
}

const sortByLabel = (
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  labelKey: string,
) => {
  const labelA = String(a[labelKey] ?? '').toLowerCase();
  const labelB = String(b[labelKey] ?? '').toLowerCase();
  return labelA.localeCompare(labelB);
};

export function BillAmountInputValue(props: GridFilterInputValueProps) {
  const { item, applyValue, focusElementRef } = props;

  const elementRef: React.Ref<any> = useRef(null);
  useImperativeHandle(focusElementRef, () => ({
    focus: () => {
      elementRef.current.querySelector('input').focus();
    },
  }));

  const handleFilterChange = (newValue: string) => {
    if (Number.isNaN(Number(newValue))) return;
    applyValue({ ...item, value: newValue });
  };

  const materialUITextFieldProps = {
    fullWidth: true,
    label: 'Value',
    placeholder: 'Filter value',
    slotProps: { inputLabel: { shrink: true } },
  };

  return (
    <NumericFormat
      allowNegative
      decimalScale={2}
      value={item.value}
      thousandSeparator
      getInputRef={elementRef}
      onValueChange={(values) => {
        const { value } = values;
        handleFilterChange(value);
      }}
      customInput={TextField}
      {...materialUITextFieldProps}
    />
  );
}

export function DateRangePickerComponent({
  item,
  applyValue,
  size = 'medium',
}: DateRangeFilterProps) {
  return (
    <DateRangePicker
      value={item.value || [null, null]}
      onChange={(newValue) => {
        applyValue({ ...item, value: newValue });
      }}
      slots={{ textField: TextField }}
      slotProps={
        size === 'small'
          ? {
              textField: {
                size: 'small',
                sx: { '& .MuiInputLabel-root': { fontSize: '0.8rem' } },
              },
            }
          : undefined
      }
    />
  );
}

export function FreeSelectorComponent(props: GridFilterInputValueProps) {
  const { item, applyValue } = props;

  const handleFilterChange = (_, newValue) => {
    applyValue({ ...item, value: newValue });
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={item.value || []}
      onChange={handleFilterChange}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Value"
          placeholder="Search..."
          sx={{
            '& .MuiInputBase-root': {
              minHeight: 30,
              padding: '1px 8px',
            },
          }}
          slotProps={{
            ...params.slotProps,
            inputLabel: { ...params.slotProps.inputLabel, shrink: true },
            htmlInput: {
              ...params.slotProps.htmlInput,
              'data-testid': 'free-solo-selector-component',
            },
          }}
        />
      )}
    />
  );
}

export const MultipleSelectorComponent = ({
  applyValue,
  service,
  item,
  disabled,
}: ComponentProps) => {
  const handleSetSelections =
    (newSelections = []) =>
    () =>
      applyValue({ ...item, value: [...newSelections] });

  const selections = item.value ?? [];
  const multipleOptions = service.data as Array<Record<string, unknown>>;
  const sortedOptions = [...multipleOptions].sort((a, b) =>
    sortByLabel(a, b, service.fieldset.label),
  );

  // MUI resets Autocomplete's inputValue to '' whenever the controlled `value` changes;
  // We control it ourselves so selecting an option doesn't clear what the user typed.
  const [searchInputValue, setSearchInputValue] = useState('');

  return (
    <Autocomplete
      id="custom-multiple-search-selector"
      multiple
      disabled={disabled}
      options={sortedOptions}
      value={selections}
      inputValue={searchInputValue}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setSearchInputValue(newInputValue);
        }
      }}
      onChange={handleSetSelections()}
      slots={{ popper: WidePopper }}
      slotProps={{
        paper: { sx: { width: 'max-content', minWidth: '100%' } },
      }}
      renderOption={(props, option) => {
        const {
          key: fSetKey,
          value: fSetValue,
          label: fSetLabel,
        } = service.fieldset;
        const key = option[fSetKey || fSetValue] || props['id'];
        const label = option[fSetLabel];
        const value = option[fSetValue];
        const checked = selections.findIndex((item) => item === value) > -1;
        return (
          <li
            {...props}
            key={key}
            onClick={handleSetSelections([
              ...selections.filter((_value) => _value !== value),
              ...(!checked ? [value] : []),
            ])}
            style={
              checked ? { ...props.style, ...SELECTED_STYLE } : props.style
            }
          >
            <Checkbox checked={checked} />
            <Typography
              style={{ display: 'inline-block', whiteSpace: 'pre-line' }}
            >
              {label}
            </Typography>
          </li>
        );
      }}
      getOptionLabel={(option) => option[service.fieldset.label] ?? ''}
      isOptionEqualToValue={(option, value) =>
        option[service.fieldset.value] === value
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Values"
          placeholder="Search..."
          slotProps={{
            ...params.slotProps,
            inputLabel: { ...params.slotProps.inputLabel, shrink: true },
          }}
          sx={{
            '& .MuiInputBase-root': {
              minHeight: 30,
              padding: '1px 8px',
            },
          }}
        />
      )}
      renderValue={(selected) =>
        selected.map((key, index) => {
          const item = service.data.find(
            (item) => item[service.fieldset.value] === key,
          );
          if (!item) return null;
          const label = item[service.fieldset.label];
          return (
            <Chip
              key={`chip-tag-${index}`}
              label={label}
              onDelete={() => {
                const newSelection = selections.filter((_id) => _id !== key);
                handleSetSelections(newSelection)();
              }}
              sx={{ height: 24, fontSize: 12, margin: '2px' }}
            />
          );
        })
      }
    />
  );
};

export const SingleSelectorComponent = ({
  applyValue,
  service,
  item,
  disabled,
}: ComponentProps) => {
  const handleChange =
    (values = []) =>
    () => {
      const selectedValue = values[0] ?? '';
      const selectedOption: any = service?.data?.find(
        (option) => option[service?.fieldset?.value] === selectedValue,
      );
      const additionalFilters = selectedOption?.additionalFilters;
      const replaceBaseFilter = selectedOption?.replaceBaseFilter;

      applyValue({
        ...item,
        value: selectedValue,
        additionalFilters,
        replaceBaseFilter,
      } as GridFilterItem);
    };

  const selection = item.value ?? '';

  const singleOptions = (service?.data ?? []) as Array<Record<string, unknown>>;
  const sortedOptions = [...singleOptions].sort((a, b) =>
    sortByLabel(a, b, service?.fieldset?.label),
  );

  return (
    <Autocomplete
      id="custom-single-search-selector"
      disabled={disabled}
      value={selection}
      onChange={handleChange()}
      options={sortedOptions}
      slots={{ popper: WidePopper }}
      slotProps={{
        paper: {
          sx: { width: 'max-content', minWidth: '100%', minHeight: 100 },
        },
      }}
      getOptionLabel={(option) => {
        if (option[service?.fieldset?.label]) {
          return option[service?.fieldset?.label] ?? '';
        } else {
          try {
            return service?.data?.find(
              (item) => item[service?.fieldset?.value] === option,
            )[service?.fieldset?.label];
          } catch {
            return '';
          }
        }
      }}
      isOptionEqualToValue={(option) => !!option[service?.fieldset?.label]}
      renderOption={(props, option) => {
        const { value: fSetValue, label: fSetLabel } = service?.fieldset;
        const key = option[fSetValue];
        const label = option[fSetLabel];
        return (
          <li
            {...props}
            key={key}
            onClick={handleChange([key])}
            style={
              selection === key
                ? { ...props.style, ...SELECTED_STYLE }
                : props.style
            }
          >
            {label}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Value"
          placeholder="Search..."
          slotProps={{
            ...params.slotProps,
            inputLabel: { ...params.slotProps.inputLabel, shrink: true },
          }}
          sx={{
            '.MuiInputBase-root': {
              maxHeight: 40,
            },
          }}
        />
      )}
    />
  );
};
